import React, { useEffect, useState } from 'react';
import { useCrmAllPrices } from '@/hooks/useCrmAllPrices';
import { formatAmount, formatInstrumentPrice } from '@/utils/formatters';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';

interface InstrumentData {
    id: string;
    symbol: string;
    name?: string;
    pip_size?: number;
    lot_size?: number;
    base_asset?: { id: string; symbol: string; name: string };
    quote_asset?: { id: string; symbol: string; name: string };
}

interface RealTimePriceProps {
    instrument: InstrumentData;
    fallbackPrice?: number;
    className?: string;
    showChange?: boolean;
    refreshInterval?: number;
}

const RealTimePrice: React.FC<RealTimePriceProps> = ({
    instrument,
    fallbackPrice = 0,
    className = '',
    showChange = false,
    refreshInterval = 1000,
}) => {
    const [priceHistory, setPriceHistory] = useState<number[]>([]);
    const [lastPrice, setLastPrice] = useState<number>(fallbackPrice);

    const { prices: pricesData, isLoading } = useCrmAllPrices([instrument.id]);

    const prices = pricesData ? Object.fromEntries(pricesData) : {};
    const currentPrice = prices[instrument.id]?.last || fallbackPrice;

    // Track price history for change calculation
    useEffect(() => {
        if (currentPrice && currentPrice !== lastPrice) {
            setPriceHistory(prev => {
                const newHistory = [...prev, currentPrice];
                // Keep only last 5 prices for change calculation
                return newHistory.length > 5 ? newHistory.slice(-5) : newHistory;
            });
            setLastPrice(currentPrice);
        }
    }, [currentPrice, lastPrice]);

    const getPriceChange = () => {
        if (priceHistory.length < 2) return 0;
        const current = priceHistory[priceHistory.length - 1];
        const previous = priceHistory[priceHistory.length - 2];
        return ((current - previous) / previous) * 100;
    };

    const formatPrice = (price: number) => {
        return formatInstrumentPrice(price, instrument);
    };

    const formatChange = (change: number) => {
        const sign = change >= 0 ? '+' : '';
        return `${sign}${formatAmount(change, 5)}%`;
    };

    const change = getPriceChange();
    const isPositive = change >= 0;

    if (isLoading && priceHistory.length === 0) {
        return <span className={`${className} text-muted-foreground`}>Loading...</span>;
    }

    return (
        <div className={`${className}`}>
            <PriceDisplayWithFlash
                price={currentPrice}
                className="font-mono"
                formatPrice={formatPrice}
            />
            {showChange && priceHistory.length >= 2 && (
                <div className={`text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {formatChange(change)}
                </div>
            )}
        </div>
    );
};

export default RealTimePrice;
